import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { TagsDashboardSection } from '../features/tags/TagsPageSections';

// Standalone Tags Dashboard page (separate route from the Tags page). Owns its
// own date range and fetches /tags/analytics, then renders the shared
// TagsDashboardSection.
export default function TagsDashboardPage() {
  const { isAdmin, isSupervisor } = useAuth();

  const formatIsoDate = (date) => new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().slice(0, 10);
  const createDefaultRange = () => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setMonth(today.getMonth() - 3);
    return {
      from_date: formatIsoDate(fromDate),
      to_date: formatIsoDate(today),
    };
  };
  const formatDateRangeLabel = (range) => {
    if (!range?.from_date || !range?.to_date) {
      return 'Last 3 months';
    }
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${formatter.format(new Date(range.from_date))} - ${formatter.format(new Date(range.to_date))}`;
  };

  const [dateRange, setDateRange] = useState(createDefaultRange);
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [allTagRecords, setAllTagRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    if (!isAdmin && !isSupervisor) return;

    setRefreshing(true);
    try {
      // Fetch analytics + the tag list together: "All Tag Records" mirrors the
      // Tags page snapshot, which sums each tag's matched_calls from /tags.
      const [res, tagsRes] = await Promise.all([
        api.getTagsAnalytics({ from_date: dateRange.from_date, to_date: dateRange.to_date }),
        api.getTags(),
      ]);
      setAnalytics(res || null);
      const tagList = tagsRes?.tags || [];
      setAllTagRecords(tagList.reduce((total, tag) => total + (Number(tag.matched_calls) || 0), 0));
    } catch {
      toast.error('Failed to load tags dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateRange.from_date, dateRange.to_date, isAdmin, isSupervisor]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (!isAdmin && !isSupervisor) {
    return <div className="text-center py-12 text-muted-foreground">Access required</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Tags Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Trending tags, frequently used tags and tag activity overview.</p>
      </div>

      <TagsDashboardSection
        dateRange={dateRange}
        isCustomDateRange={isCustomDateRange}
        onDateRangeChange={(nextRange) => {
          setIsCustomDateRange(true);
          setDateRange((prev) => ({
            from_date: nextRange.from_date || prev.from_date,
            to_date: nextRange.to_date || nextRange.from_date || prev.to_date,
          }));
        }}
        onDateRangeClear={() => {
          setDateRange(createDefaultRange());
          setIsCustomDateRange(false);
        }}
        refreshing={refreshing}
        onRefresh={fetchAnalytics}
        loading={loading}
        analytics={analytics}
        allTagRecords={allTagRecords}
        rangeLabel={isCustomDateRange ? formatDateRangeLabel(dateRange) : 'Last 3 months'}
      />
    </div>
  );
}
